import { MAP_CLUSTER_MAX_ZOOM, MAP_CLUSTER_MIN_ZOOM, MAP_CLUSTER_RADIUS, MAP_ONE_POINT_ZOOM } from 'config/constants';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { useMapContext } from 'context/Map/useMapContext';
import { BBox } from 'geojson';
import { Bounds } from 'pigeon-maps';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as Supercluster from 'supercluster';
import { PointFeature } from 'supercluster';
import { MapMarkerType, MapProps, SuperclusterClusterMarkerType, SuperclusterPointMarketType } from 'types/map';
import useSupercluster from 'use-supercluster';

type UsePigeonMapProps = Pick<
    MapProps,
    'markers' | 'clickOnMarkerHandler' | 'shouldChangeFitToBounds' | 'minimumFitToBoundsZoom' | 'clusterRadius'
>;

const FIT_BOUNDS_PADDING = 80;
const WORLD_TILE_SIZE = 256;

type MapSize = {
    width: number;
    height: number;
};

const latitudeToRadians = (latitude: number) => {
    const sine = Math.sin((latitude * Math.PI) / 180);

    return Math.log((1 + sine) / (1 - sine)) / 2;
};

const calculateFitToBoundsZoom = (minLat: number, maxLat: number, minLng: number, maxLng: number, mapSize: MapSize) => {
    const mapWidth = Math.max(mapSize.width - FIT_BOUNDS_PADDING, 1);
    const mapHeight = Math.max(mapSize.height - FIT_BOUNDS_PADDING, 1);
    const latitudeFraction = Math.max(
        (latitudeToRadians(maxLat) - latitudeToRadians(minLat)) / (2 * Math.PI),
        Number.EPSILON,
    );
    const longitudeDifference = maxLng - minLng;
    const longitudeFraction = Math.max(
        (longitudeDifference < 0 ? longitudeDifference + 360 : longitudeDifference) / 360,
        Number.EPSILON,
    );
    const latitudeZoom = Math.log2(mapHeight / WORLD_TILE_SIZE / latitudeFraction);
    const longitudeZoom = Math.log2(mapWidth / WORLD_TILE_SIZE / longitudeFraction);

    return Math.max(
        Math.min(Math.floor(latitudeZoom), Math.floor(longitudeZoom), MAP_CLUSTER_MAX_ZOOM),
        MAP_CLUSTER_MIN_ZOOM,
    );
};

const markerMapper = (marker: MapMarkerType): PointFeature<SuperclusterPointMarketType> => ({
    type: 'Feature',
    properties: { ...marker },
    geometry: {
        type: 'Point',
        coordinates: [marker.lng, marker.lat],
    },
});

export const usePigeonMap = ({
    markers,
    clickOnMarkerHandler,
    shouldChangeFitToBounds,
    minimumFitToBoundsZoom,
    clusterRadius = MAP_CLUSTER_RADIUS,
}: UsePigeonMapProps) => {
    const { mapSetting } = useDomainConfig();
    const [{ activeMarkerUuid, center, zoom, isFitToBounds }, dispatch] = useMapContext();
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const [mapSize, setMapSize] = useState<MapSize | null>(null);

    const defaultBounds: BBox = [mapSetting.longitude, mapSetting.latitude, mapSetting.longitude, mapSetting.latitude];
    const [bounds, setBounds] = useState<BBox>(defaultBounds);

    const pigeonMarkers: PointFeature<SuperclusterPointMarketType>[] = useMemo(
        () => markers.map(markerMapper),
        [markers],
    );

    const clusterOptions = useMemo(
        () => ({ radius: clusterRadius, maxZoom: MAP_CLUSTER_MAX_ZOOM, minZoom: MAP_CLUSTER_MIN_ZOOM }),
        [clusterRadius],
    );
    const { clusters, supercluster } = useSupercluster<SuperclusterPointMarketType, SuperclusterClusterMarkerType>({
        points: pigeonMarkers,
        bounds,
        zoom,
        options: clusterOptions,
    });

    const activePoint = useMemo(() => {
        return pigeonMarkers.find((marker) => marker.properties.identifier === activeMarkerUuid);
    }, [activeMarkerUuid, pigeonMarkers]);

    const isActivePointValidForPopup =
        activePoint?.properties.storeName && activePoint.properties.storeStreet && activePoint.properties.storeCity;

    const handleBoundsChanged = ({
        center: nextCenter,
        zoom: nextZoom,
        bounds: nextBounds,
    }: {
        center: [number, number];
        bounds: Bounds;
        zoom: number;
        initial: boolean;
    }) => {
        const boundingBoxCoordinates: BBox = [nextBounds.sw[1], nextBounds.sw[0], nextBounds.ne[1], nextBounds.ne[0]];
        setBounds((currentBounds) =>
            currentBounds.every((coordinate, index) => coordinate === boundingBoxCoordinates[index])
                ? currentBounds
                : boundingBoxCoordinates,
        );

        dispatch({ type: 'setZoom', payload: nextZoom });
        dispatch({ type: 'setCenter', payload: { lat: nextCenter[0], lng: nextCenter[1] } });
    };

    const onPointClickHandler = (identifier: string, pointId: number, isCluster: boolean = false) => {
        if (isCluster) {
            try {
                const leaves = supercluster?.getLeaves(pointId, Infinity);
                fitMapToPoints(leaves ?? []);
            } catch {
                // Nonexistent point id
            }
        } else {
            clickOnMarkerHandler?.(identifier);
        }
    };

    const fitMapToPoints = (points: Supercluster.PointFeature<SuperclusterPointMarketType>[]) => {
        if (points.length === 0) {
            return;
        }

        const pointBounds = points.reduce(
            (acc, point) => {
                acc.minLng = Math.min(acc.minLng, point.geometry.coordinates[0]);
                acc.maxLng = Math.max(acc.maxLng, point.geometry.coordinates[0]);
                acc.minLat = Math.min(acc.minLat, point.geometry.coordinates[1]);
                acc.maxLat = Math.max(acc.maxLat, point.geometry.coordinates[1]);

                return acc;
            },
            { minLng: Infinity, maxLng: -Infinity, minLat: Infinity, maxLat: -Infinity },
        );
        setBounds([pointBounds.minLng, pointBounds.minLat, pointBounds.maxLng, pointBounds.maxLat]);
        const newCenter = {
            lat: (pointBounds.minLat + pointBounds.maxLat) / 2,
            lng: (pointBounds.minLng + pointBounds.maxLng) / 2,
        };
        const calculatedZoom =
            points.length === 1 || mapSize === null
                ? MAP_ONE_POINT_ZOOM
                : calculateFitToBoundsZoom(
                      pointBounds.minLat,
                      pointBounds.maxLat,
                      pointBounds.minLng,
                      pointBounds.maxLng,
                      mapSize,
                  );
        const newZoom = Math.max(calculatedZoom, minimumFitToBoundsZoom ?? MAP_CLUSTER_MIN_ZOOM);

        dispatch({ type: 'setZoom', payload: newZoom });
        dispatch({ type: 'setCenter', payload: newCenter });
    };

    useEffect(() => {
        const mapContainer = mapContainerRef.current;

        if (mapContainer === null) {
            return undefined;
        }

        const updateMapSize = () => {
            const { width, height } = mapContainer.getBoundingClientRect();
            setMapSize((currentMapSize) =>
                currentMapSize?.width === width && currentMapSize.height === height
                    ? currentMapSize
                    : { width, height },
            );
        };
        const resizeObserver = new ResizeObserver(updateMapSize);

        updateMapSize();
        resizeObserver.observe(mapContainer);

        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        if (activePoint && !shouldChangeFitToBounds) {
            return;
        }

        let newZoom = mapSetting.zoom;
        let newCenter = { lat: mapSetting.latitude, lng: mapSetting.longitude };

        if (pigeonMarkers.length === 1 || activePoint) {
            const marker = activePoint ? activePoint : pigeonMarkers[0];
            newZoom = MAP_ONE_POINT_ZOOM;
            newCenter = { lat: marker.properties.lat, lng: marker.properties.lng };
        } else if (pigeonMarkers.length > 1) {
            if (isFitToBounds && mapSize !== null) {
                const bounds = pigeonMarkers.reduce(
                    (acc, { properties: { lat, lng } }) => {
                        acc.minLat = Math.min(acc.minLat, lat);
                        acc.maxLat = Math.max(acc.maxLat, lat);
                        acc.minLng = Math.min(acc.minLng, lng);
                        acc.maxLng = Math.max(acc.maxLng, lng);

                        return acc;
                    },
                    { minLat: Infinity, maxLat: -Infinity, minLng: Infinity, maxLng: -Infinity },
                );

                newCenter = { lat: (bounds.minLat + bounds.maxLat) / 2, lng: (bounds.minLng + bounds.maxLng) / 2 };
                newZoom = Math.max(
                    calculateFitToBoundsZoom(bounds.minLat, bounds.maxLat, bounds.minLng, bounds.maxLng, mapSize),
                    minimumFitToBoundsZoom ?? MAP_CLUSTER_MIN_ZOOM,
                );
            }
        }

        dispatch({ type: 'setZoom', payload: newZoom });
        dispatch({ type: 'setCenter', payload: newCenter });
    }, [
        pigeonMarkers,
        isFitToBounds,
        mapSetting,
        dispatch,
        activePoint,
        shouldChangeFitToBounds,
        minimumFitToBoundsZoom,
        mapSize,
    ]);

    return {
        clusters,
        center,
        zoom,
        bounds,
        handleBoundsChanged,
        activeMarkerUuid,
        activePoint,
        isActivePointValidForPopup,
        onPointClickHandler,
        pigeonMarkers,
        mapContainerRef,
    };
};
